import { flexRender } from "@tanstack/react-table";
import type React from "react";
import { twJoin } from "tailwind-merge";

import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

import { useCustomTable } from "./CustomTableContext";
import { type DashboardHeader, getTableCellWidth } from "./helpers";
import { TableSortIcon } from "./TableSortIcon";

type TableHeaderCellProps<TColumnData extends {}> = {
  header: DashboardHeader<TColumnData>;
  isLastColumn: boolean;
};

export function TableHeaderCell<TColumnData extends {}>({
  header,
  isLastColumn,
}: TableHeaderCellProps<TColumnData>): React.ReactNode {
  const { unfilledWidth } = useCustomTable();
  const isColumnAction = header.column.columnDef.meta?.isAction;
  const isColumnVisualIndicator = header.column.columnDef.meta?.isVisualIndicator;
  const cellAlignment = header.column.columnDef.meta?.cellAlignment;
  const canSort = !isColumnAction && !isColumnVisualIndicator && header.column.getCanSort();
  const isSorted = header.column.getIsSorted();

  const canResize = !isLastColumn && !isColumnAction && !isColumnVisualIndicator && header.column.getCanResize();
  const isResizing = header.column.getIsResizing();

  const width = getTableCellWidth(header) + (isLastColumn ? unfilledWidth : 0);

  const handleToggleSorting = (e: React.MouseEvent) => {
    header.column.getToggleSortingHandler()?.(e);
  };
  const label = typeof header.column.columnDef.header === "string" ? header.column.columnDef.header : undefined;

  return (
    <RuntimeWidth width={width}>
      {(widthAttributes) => (
        <th
          {...widthAttributes}
          className={twJoin(
            "relative border-r border-r-border-default text-left align-bottom whitespace-nowrap text-text-primary capitalize last:border-r-0",
            cellAlignment === "center" && "text-center",
            cellAlignment === "end" && "text-right",
          )}
          key={header.id}
          colSpan={header.colSpan}
        >
          {!header.isPlaceholder && (
            <div
              className={twJoin(
                "flex items-center gap-space-sm px-space-sm py-space-md @md:px-space-md",
                isColumnVisualIndicator && "px-table-reset!",
                canSort && "duration-table-interaction cursor-pointer transition-colors hocus:bg-background-secondary",
              )}
              role={canSort ? "button" : undefined}
              tabIndex={canSort ? 0 : undefined}
              onClick={handleToggleSorting}
              onKeyDown={(e) => {
                if (canSort && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  handleToggleSorting(e as unknown as React.MouseEvent);
                }
              }}
              aria-label={label && `Toggle sorting by ${label}`}
            >
              <span
                className={twJoin(
                  "w-full truncate text-caption-emphasis",
                  cellAlignment === "center" && "text-center",
                  cellAlignment === "end" && "text-right",
                  isSorted && "text-text-brand-secondary",
                )}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </span>
              {canSort && (
                <span className="shrink-0">
                  <TableSortIcon sorted={!!isSorted} desc={isSorted === "desc"} />
                </span>
              )}
            </div>
          )}
          {/* Resize handle */}
          {canResize && (
            <div
              className={twJoin(
                "absolute top-0 right-0 z-table-resize-handle h-full w-table-resize-handle cursor-col-resize touch-none select-none",
                "opacity-0 transition-opacity group-hover/header:opacity-100",
                "bg-background-tertiary hover:bg-background-brand-secondary",
                isResizing && "bg-text-brand-secondary opacity-100",
              )}
              onMouseDown={(e) => {
                e.stopPropagation();
                header.getResizeHandler()(e);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                header.getResizeHandler()(e);
              }}
            />
          )}
        </th>
      )}
    </RuntimeWidth>
  );
}
